import React from 'react'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'

import { MODE_IDS } from 'common/utils/constants'

import { NO_RESULTS_MODE } from 'tickets/utils/constants'

export const partialResultsProviderConnectionErrorMsg = (<FormattedMessage
  id="errors.partialResultsProviderConnectionError"
  description="Error message describing that only some ticket results are available because some providers are unavailable"
  defaultMessage="You are only seeing some search results because the connection to one or more providers is temporarily unavailable. Please try again later to see all results"
/>)

export const noResultsProviderConnectionErrorMsg = (<FormattedMessage
  id="errors.noResultsProviderConnectionError"
  description="Error message describing that no results were found because providers are unavailable"
  defaultMessage="No results have been found because the connection to one or more providers is temporarily unavailable. Please try again later to see all results"
/>)

export const tooFarInFutureMsg = (<FormattedMessage
  id="errors.tooFarInFuture"
  description="Error message that's shown when ticket search dates are too far in future to get results"
  defaultMessage="Unfortunately, we couldn't find any tickets for the dates/times you entered. Please consider changing your dates and trying again."
/>)

export const getMaxDaysInAdvanceMsg = (maxDaysInAdvance) => {
  if (maxDaysInAdvance == 365) {
    return (<FormattedHTMLMessage
      id="errors.maxSearchDateYearSingle"
      description="Error message that tells a user when the maximum search date is"
      defaultMessage="<b>Please note:</b> Tickets for this service are usually available around 1 year in advance"
    />)
  }
  if (maxDaysInAdvance % 365 == 0) {
    return (<FormattedHTMLMessage
      id="errors.maxSearchDateYearPlural"
      description="Error message that tells a user when the maximum search date is"
      defaultMessage="<b>Please note:</b> Tickets for this service are usually available around {maxYearsInAdvance} years in advance"
      values={{
        maxYearsInAdvance: maxDaysInAdvance / 365,
      }}
    />)
  }
  if (maxDaysInAdvance === 1) {
    return (<FormattedHTMLMessage
      id="errors.maxSearchDateDaySingle"
      description="Error message that tells a user when the maximum search date is"
      defaultMessage="<b>Please note:</b> Tickets for this service are usually available around 1 day in advance"
    />)
  }
  return (<FormattedHTMLMessage
    id="errors.maxSearchDateDayPlural"
    description="Error message that tells a user when the maximum search date is"
    defaultMessage="<b>Please note:</b> Tickets for this service are usually available around {maxDaysInAdvance} days in advance"
    values={{
      maxDaysInAdvance,
    }}
  />)
}

export const getTransferMinHoursErrorMsg = (minHours) => (<FormattedMessage
  id="errors.airportTransferMinHours"
  description="Error message that tells a user airport transfer must be searched for some hours in the future"
  defaultMessage="Airport Transfers may not be available within {minHours} hours of departure."
  values={{ minHours }}
/>)

export const invalidSearchDateMsg = (<FormattedMessage
  id="errors.invalidSearchDate"
  description="Error when perform ticket search for invalid search date"
  defaultMessage="You may be able to find tickets for an earlier date."
/>)

export const noResultsHeaderMsg = (modeId) => {
  switch (modeId) {
    case MODE_IDS.train:
      return (<FormattedMessage
        id="errors.noTrainResultsHeader"
        description="Header for a 'no ticket results' message"
        defaultMessage="No train tickets found."
      />)
    case MODE_IDS.bus:
      return (<FormattedMessage
        id="errors.noBusResultsHeader"
        description="Header for a 'no ticket results' message"
        defaultMessage="No bus tickets found."
      />)
    case MODE_IDS.ferry:
      return (<FormattedMessage
        id="errors.noFerryResultsHeader"
        description="Header for a 'no ticket results' message"
        defaultMessage="No ferry tickets found."
      />)
    case MODE_IDS.plane:
      return (<FormattedMessage
        id="errors.noPlaneResultsHeader"
        description="Header for a 'no ticket results' message"
        defaultMessage="No plane tickets found."
      />)
    case MODE_IDS.airport_transfer:
      return (<FormattedMessage
        id="errors.noTransferResultsHeader"
        description="Header for a 'no ticket results' message"
        defaultMessage="No transfer tickets found."
      />)
    case '':
    case NO_RESULTS_MODE:
      return (<FormattedMessage
        id="errors.noResultsHeader"
        description="Header for a 'no ticket results' message"
        defaultMessage="No tickets found."
      />)
    default:
      return (<FormattedMessage
        id="errors.defaultNoResultsHeader"
        description="Header for a 'no ticket results' message"
        defaultMessage="No {transportType} tickets found."
        values={{ transportType: modeId }}
      />)
  }
}

export const unexpectedErrorHeaderMsg = (<FormattedMessage
  id="errors.unexpectedErrorHeader"
  description="Header for an encountered unexpected error"
  defaultMessage="Unexpected error"
/>)

export const unexpectedErrorBodyMsg = (<FormattedMessage
  id="errors.unexpectedErrorBody"
  description="Gives the user detail about why an unexpected error has occurred"
  defaultMessage="Unfortunately Rome2Rio's ticket search is experiencing some issues right now. Please try refreshing the page, if that doesn't work you might need to try again in a few minutes."
/>)

export const noResultsBodyMsg = (<FormattedMessage
  id="errors.noResultsBody"
  description="Tells the user we were unable to find tickets for their search"
  defaultMessage="Sorry! We were not able to find any tickets that matched your search."
/>)

export const noResultsDetailedBodyMsg = (<FormattedMessage
  id="errors.noResultsDetailedBody"
  description="Gives the user details about why we were unable to find tickets for their search"
  defaultMessage="Either the tickets you are looking for are not available for purchase yet or Rome2Rio does not currently sell tickets for this route. Click below to explore this route and see more options."
/>)

export const noTicketsAvailableYetMsg = (<FormattedMessage
  id="errors.noTicketsAvailableYet"
  description="Informs the user that no tickets are available for purchase yet"
  defaultMessage="Tickets not available yet"
/>)

export const stillNeedHelpMsg = (
  <FormattedMessage
    id="errors.stillNeedHelp"
    description="A help message redirecting the user to more information"
    defaultMessage="Still need help?"
  />
)

export const noSchedulesFoundMsg = (<FormattedMessage
  id="errors.noSchedulesFound"
  description="Error message that tells the user no tickets were found for the provided journey dates"
  defaultMessage="Unfortunately, we couldn't find any tickets for the dates/times you provided. Please consider changing your dates and trying again."
/>)
